/**
 * Strip HTML tags and Markdown syntax, returning plain text.
 * Shared across HistoryArea, PreviewArea, and future components.
 */
export function stripMarkupAndMarkdown(text: string): string {
  let plain = text;
  // Remove HTML tags
  if (/<[a-z][\s\S]*>/i.test(plain)) {
    const doc = new DOMParser().parseFromString(plain, 'text/html');
    plain = doc.body.textContent || '';
  }
  // Remove markdown syntax
  return plain
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/`{1,3}(.+?)`{1,3}/g, '$1')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/^\s*>\s+/gm, '')
    .replace(/!\[.*?\]\(.+?\)/g, '')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    .trim();
}

/**
 * Format a timestamp into a human-readable relative time string.
 */
export function formatTime(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

/**
 * Detects if a string contains HTML tags.
 */
export function isHtml(text: string): boolean {
  return /^\s*<[a-z][\s\S]*>/i.test(text);
}

/**
 * Detects if a string contains Markdown syntax.
 */
export function isMarkdown(text: string): boolean {
  const patterns = [
    /^#{1,6}\s+.+$/m,          // Headers
    /^\s*[-*+]\s+.+$/m,        // Unordered lists
    /^\s*\d+\.\s+.+$/m,        // Ordered lists
    /\*\*.+?\*\*/,             // Bold
    /\[.+?\]\(.+?\)/,          // Links
    /^>\s+.+$/m,               // Blockquotes
    /```[\s\S]*?```/,          // Code blocks
    /`.*?`/                    // Inline code
  ];
  return patterns.some(p => p.test(text));
}

/**
 * Detects if a string is a valid JSON.
 */
export function isJson(text: string): boolean {
  if (!text || text.length < 2) return false;
  const trimmed = text.trim();
  if (!((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']')))) {
    return false;
  }
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
}

/**
 * Detects if a string is a valid URL.
 */
export function isUrl(text: string): boolean {
  if (!text || text.length < 4) return false;
  // Improved regex for URL including localhost, IPs, ports, query params, and fragments
  // Strictly require protocol as per test expectations
  const urlRegex = /^(https?:\/\/)(localhost|([\da-z.-]+)\.([a-z.]{2,6})|(\d{1,3}\.){3}\d{1,3})(:\d+)?([/?#][\w\-.~!$&'()*+,;=:@%]*)*\/?$/i;
  
  return urlRegex.test(text.trim());
}
